import { apiGet, apiPost, apiDelete } from './client';
import type {
  AiAgentResponseSchema,
  AiResponseSchema,
  OperationResult,
  PagedItemsResult,
} from './types';

// ───────────────────────────────────────────────────────────────────────────
// agentResponseSchemas — client for the ai.AiAgentResponseSchema junction
//
// The junction links one agent to one or more rows in ai.AiResponseSchema
// with UsageGuidance + SortOrder. The runtime takes the row with the
// lowest SortOrder as the agent's default response schema for direct
// chats (Phase 3a wire-up).
//
// User-facing label: "Output Format" — not "Response Schema". The clinical
// audience reads better without the JSON-schema jargon.
// ───────────────────────────────────────────────────────────────────────────

/**
 * All Output Format links for one agent, ordered by SortOrder ascending
 * (lowest = default). The eager-loaded <c>aiResponseSchema</c> nav property
 * is populated by the server's <c>ByAgent</c> endpoint, so the UI can
 * render schema name + description without a second round-trip.
 */
export async function getOutputFormatsForAgent(
  agentId: number,
): Promise<AiAgentResponseSchema[]> {
  const result = await apiGet<OperationResult<AiAgentResponseSchema[]>>(
    `/api/AiAgentResponseSchema/ByAgent/${agentId}`,
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to load Output Formats for agent');
  }
  return result.data ?? [];
}

export interface OutputFormatInput {
  id?: number;
  aiAgentId: number;
  aiResponseSchemaId: number;
  usageGuidance?: string;
  sortOrder: number;
}

export async function addOutputFormat(input: OutputFormatInput): Promise<void> {
  const result = await apiPost<OperationResult>('/api/AiAgentResponseSchema/Add', {
    aiAgentId: input.aiAgentId,
    aiResponseSchemaId: input.aiResponseSchemaId,
    usageGuidance: input.usageGuidance ?? '',
    sortOrder: input.sortOrder,
  });
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to add Output Format');
  }
}

export async function updateOutputFormat(input: OutputFormatInput): Promise<void> {
  if (input.id == null) throw new Error('Output Format id is required for update');
  const result = await apiPost<OperationResult>('/api/AiAgentResponseSchema/Update', {
    id: input.id,
    aiAgentId: input.aiAgentId,
    aiResponseSchemaId: input.aiResponseSchemaId,
    usageGuidance: input.usageGuidance ?? '',
    sortOrder: input.sortOrder,
  });
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to update Output Format');
  }
}

export async function removeOutputFormat(id: number): Promise<void> {
  const result = await apiDelete<OperationResult>(
    `/api/AiAgentResponseSchema/Remove/${id}`,
  );
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to remove Output Format');
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Schema catalog client — for the picker that lets the user select which
// schema to link. The catalog itself is read-only from the UI; rows are
// seeded by C# code (A2AResponseSchemaRegistry.SyncToDatabase) or by the
// Phase 1+2b PeerRendering_* provisioning tests.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Search the catalog of available response schemas (ai.AiResponseSchema).
 * Used by the "Add Output Format" drawer to populate the picker.
 */
export async function searchResponseSchemas(
  search = '',
  pageNo = 1,
  pageSize = 50,
): Promise<AiResponseSchema[]> {
  const result = await apiGet<OperationResult<PagedItemsResult<AiResponseSchema>>>(
    '/api/AiResponseSchema/Search',
    { search, pageNo, pageSize },
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to load response schemas');
  }
  return result.data.data ?? [];
}
